import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Inject,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChildren,
} from '@angular/core';
import { Store } from 'redux';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

import {
    ConfigView,
    flattenMetrics,
    Metric,
    QuickSwitch,
    QuickSwitchItem,
    QuickSwitchType,
    QuickSwitchTypeCriteriaIdTypeMap,
} from '../insight/config.insight.model';
import {
    visualRetrieveData,
    visualRetrieveDataCancel,
} from '../insight/insight.action';
import { VisualState } from '../insight/insight.state';
import { OptionalColumn } from '../insight/optional-column.insight.model';
import { DataFilterOptions } from '../insight/performance-data/data-filter-options.pd.model';
import {
    CriteriaIdType,
    PerformanceDataViewType,
    PerformanceDataViewTypeArgsMap,
} from '../insight/performance-data/metric.pd.model';
import { VisualItemInsightComponent } from '../insight/visual-item.insight.component';
import {
    LayoutQuery,
    VisualItemClickEvent,
} from '../insight/visual-item.insight.model';
import { StateToProps, storeObserver } from '../store';
import { STORE } from '../store.metadata';
import { PeriodType } from '../widget/date-range.widget.model';

@Component({
    selector: '[cmpsPerformanceInsightVisual]',
    templateUrl: './visual.pi.component.html',
})
export class VisualPerformanceInsightComponent
    implements OnChanges, OnInit, OnDestroy, StateToProps<VisualState>
{
    @ViewChildren(VisualItemInsightComponent)
    items: QueryList<VisualItemInsightComponent>;

    @Input() view: ConfigView;
    @Input() metrics: Metric[];
    @Input() periodType: PeriodType;
    @Input() eventDate: Date;
    @Input() levelOpt: QuickSwitchItem;
    @Input() quickSwitch: QuickSwitch;
    @Input() initialCriteriaIdType: CriteriaIdType;
    @Input() initialDataFilterOpts: DataFilterOptions;
    @Input() optionalColumns: OptionalColumn[];
    @Output() itemClick: EventEmitter<VisualItemClickEvent>;
    private _storeSub: Subscription;
    state: VisualState;
    layoutQuery: LayoutQuery;

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _changeDetectorRef: ChangeDetectorRef,
        @Inject(STORE) private readonly _store: Store,
    ) {
        this.itemClick = new EventEmitter();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.state) return;
        // eslint-disable-next-line no-prototype-builtins
        if (
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('view') ||
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('periodType') ||
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('eventDate') ||
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('levelOpt')
        ) {
            this.propsToDispatch();
        }
    }

    ngOnInit(): void {
        this._storeSub = storeObserver<any, VisualState>(
            this._store,
            (state) => state.insight.visual,
        )
            .pipe(
                tap((state) =>
                    this._ngZone.run(() => {
                        const prevState = this.state;
                        this.state = state;
                        this.stateToProps(prevState);
                    }),
                ),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this._store.dispatch(visualRetrieveDataCancel());
        if (this._storeSub) {
            this._storeSub.unsubscribe();
        }
        if (this.itemClick) {
            this.itemClick.complete();
        }
    }

    stateToProps(prevState?: VisualState): void {
        if (!prevState) {
            this.propsToDispatch();
        }
    }

    propsToDispatch(): void {
        if (this.layoutQuery) {
            this.layoutQuery.unbind();
        }
        this.layoutQuery = new LayoutQuery();
        this.layoutQuery.bind(this.view, () => {
            this.items.forEach((e) => e._layoutRows());
            this._changeDetectorRef.detectChanges();
        });
        this.layoutQuery.style();

        this._store.dispatch(visualRetrieveDataCancel());

        const ct =
            QuickSwitchTypeCriteriaIdTypeMap[this.quickSwitch.quickSwitchType];
        let vt;
        if (this.quickSwitch.quickSwitchType === QuickSwitchType.MARKET) {
            vt = PerformanceDataViewType.MARKET;
        }
        if (
            ct === CriteriaIdType.LOB_ID &&
            this.initialCriteriaIdType === CriteriaIdType.LOB_ID
        ) {
            vt = PerformanceDataViewType.LOB;
        }
        if (
            ct === CriteriaIdType.REGION_ID &&
            this.initialCriteriaIdType === CriteriaIdType.REGION_ID
        ) {
            vt = PerformanceDataViewType.REGION;
        }
        if (
            ct === CriteriaIdType.AREA_ID &&
            this.initialCriteriaIdType === CriteriaIdType.AREA_ID
        ) {
            vt = PerformanceDataViewType.AREA;
        }
        if (
            ct === CriteriaIdType.SITE_ID &&
            this.initialCriteriaIdType === CriteriaIdType.SITE_ID
        ) {
            vt = PerformanceDataViewType.SITE;
        }
        if (
            ct === CriteriaIdType.PEER_GROUP_ID &&
            this.initialCriteriaIdType === CriteriaIdType.PEER_GROUP_ID
        ) {
            vt = PerformanceDataViewType.PEER_GROUP;
        }
        if (
            ct === CriteriaIdType.SUPER_GROUP_ID &&
            this.initialCriteriaIdType === CriteriaIdType.SUPER_GROUP_ID
        ) {
            vt = PerformanceDataViewType.SUPER_GROUP;
        }
        if (
            (ct === CriteriaIdType.LOB_ID &&
                this.initialCriteriaIdType === CriteriaIdType.REGION_ID) ||
            (ct === CriteriaIdType.REGION_ID &&
                this.initialCriteriaIdType === CriteriaIdType.LOB_ID)
        ) {
            vt = PerformanceDataViewType.REGION_LOB;
        }
        if (
            (ct === CriteriaIdType.LOB_ID &&
                this.initialCriteriaIdType === CriteriaIdType.AREA_ID) ||
            (ct === CriteriaIdType.AREA_ID &&
                this.initialCriteriaIdType === CriteriaIdType.LOB_ID)
        ) {
            vt = PerformanceDataViewType.AREA_LOB;
        }
        if (
            (ct === CriteriaIdType.LOB_ID &&
                this.initialCriteriaIdType === CriteriaIdType.SITE_ID) ||
            (ct === CriteriaIdType.SITE_ID &&
                this.initialCriteriaIdType === CriteriaIdType.LOB_ID)
        ) {
            vt = PerformanceDataViewType.SITE_LOB;
        }
        if (
            (ct === CriteriaIdType.PEER_GROUP_ID &&
                this.initialCriteriaIdType === CriteriaIdType.REGION_ID) ||
            (ct === CriteriaIdType.REGION_ID &&
                this.initialCriteriaIdType === CriteriaIdType.PEER_GROUP_ID)
        ) {
            vt = PerformanceDataViewType.REGION_PEER_GROUP;
        }
        if (
            (ct === CriteriaIdType.PEER_GROUP_ID &&
                this.initialCriteriaIdType === CriteriaIdType.AREA_ID) ||
            (ct === CriteriaIdType.AREA_ID &&
                this.initialCriteriaIdType === CriteriaIdType.PEER_GROUP_ID)
        ) {
            vt = PerformanceDataViewType.AREA_PEER_GROUP;
        }
        if (
            (ct === CriteriaIdType.PEER_GROUP_ID &&
                this.initialCriteriaIdType === CriteriaIdType.SITE_ID) ||
            (ct === CriteriaIdType.SITE_ID &&
                this.initialCriteriaIdType === CriteriaIdType.PEER_GROUP_ID)
        ) {
            vt = PerformanceDataViewType.SITE_PEER_GROUP;
        }
        if (
            (ct === CriteriaIdType.SUPER_GROUP_ID &&
                this.initialCriteriaIdType === CriteriaIdType.REGION_ID) ||
            (ct === CriteriaIdType.REGION_ID &&
                this.initialCriteriaIdType === CriteriaIdType.SUPER_GROUP_ID)
        ) {
            vt = PerformanceDataViewType.REGION_SUPER_GROUP;
        }
        if (
            (ct === CriteriaIdType.SUPER_GROUP_ID &&
                this.initialCriteriaIdType === CriteriaIdType.AREA_ID) ||
            (ct === CriteriaIdType.AREA_ID &&
                this.initialCriteriaIdType === CriteriaIdType.SUPER_GROUP_ID)
        ) {
            vt = PerformanceDataViewType.AREA_SUPER_GROUP;
        }
        if (
            (ct === CriteriaIdType.SUPER_GROUP_ID &&
                this.initialCriteriaIdType === CriteriaIdType.SITE_ID) ||
            (ct === CriteriaIdType.SITE_ID &&
                this.initialCriteriaIdType === CriteriaIdType.SUPER_GROUP_ID)
        ) {
            vt = PerformanceDataViewType.SITE_SUPER_GROUP;
        }
        const ps = PerformanceDataViewTypeArgsMap[vt];

        this._store.dispatch(
            visualRetrieveData({
                identifier: this.levelOpt.name,
                viewType: vt,
                displayNameCriteriaIdType: ps.primary,
                outputCriteriaIdType: ps.primary,
                inputCriteriaIdTypes: null,
                periodType: this.periodType,
                date: this.eventDate,
                lobIdList: [...(this.initialDataFilterOpts.lobIdList || [])],
                regionIdList: [
                    ...(this.initialDataFilterOpts.regionIdList || []),
                ],
                areaIdList: [...(this.initialDataFilterOpts.areaIdList || [])],
                siteIdList: [...(this.initialDataFilterOpts.siteIdList || [])],
                peerGroupIdList: [
                    ...(this.initialDataFilterOpts.peerGroupIdList || []),
                ],
                superGroupIdList: [
                    ...(this.initialDataFilterOpts.superGroupIdList || []),
                ],
                metricIdList: flattenMetrics(this.view.pillars),
            }),
        );
    }

    onItemClick($event: VisualItemClickEvent): void {
        this.itemClick.emit($event);
    }
}
